// pages/Processing.jsx
// Lists orders in the "Processing" stage of the ORDER workflow (serviceType === 'order').
// Update action opens a drawer to upload photos and mark serviceType as 'ready'.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useSeller } from '../contexts/SellerContext';
import PageHeader from '../components/PageHeader';
import Button from '../ui/components/Button';
import { Camera, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './OrderStatus.css';

const STATUS = 'Processing';
const NEXT_STATUS = 'Assortment';

const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const CLOUDINARY_UPLOAD_PRESET = 'jmiseller';
const CLOUDINARY_API_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`;

const toDateObj = (val) => {
  if (!val) return null;
  if (typeof val.toDate === 'function') return val.toDate();
  if (val instanceof Date) return val;
  if (typeof val === 'object' && typeof val.seconds === 'number') return new Date(val.seconds * 1000);
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDate = (val) => {
  const d = toDateObj(val);
  if (!d) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = String(d.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
};

const subtractDays = (dateVal, days) => {
  const d = toDateObj(dateVal);
  if (!d || !Number.isFinite(days) || days <= 0) return null;
  d.setDate(d.getDate() - days);
  return d;
};

const computeUpdateDateFromLead = (completeOrderDateStr, createdAt) => {
  const complete = toDateObj(completeOrderDateStr);
  const created = toDateObj(createdAt);
  if (!complete || !created) return null;
  const leadDays = (complete.getTime() - created.getTime()) / (1000 * 60 * 60 * 24);
  return subtractDays(completeOrderDateStr, leadDays * 0.6);
};

const computeUpdateDateFromSla = (completeOrderDateStr, orderServeSla) => {
  const days = parseFloat(orderServeSla);
  return subtractDays(completeOrderDateStr, days * 0.6);
};

const Processing = () => {
  const { seller } = useSeller();
  const sellerId = seller?.sellerId;
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingId, setPendingId] = useState(null);
  const [updateDrawerOpen, setUpdateDrawerOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [completedOrderIds, setCompletedOrderIds] = useState(new Set());

  const fetchOrders = useCallback(async () => {
    if (!sellerId) return;

    try {
      setLoading(true);
      setError(null);
      const q = query(
        collection(db, 'orderList'),
        where('sellerId', '==', sellerId),
        where('serviceType', '==', 'order'),
        where('orderStatus', '==', STATUS)
      );
      const snapshot = await getDocs(q);

      const fetchedOrders = [];
      snapshot.forEach((docSnap) => {
        fetchedOrders.push({
          firestoreId: docSnap.id,
          ...docSnap.data(),
        });
      });
      setOrders(fetchedOrders);
    } catch (err) {
      console.error('Error fetching processing orders:', err);
      setError('Failed to load processing orders.');
    } finally {
      setLoading(false);
    }
  }, [sellerId]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleImageUpload = async (files) => {
    setUploading(true);
    const uploadPromises = Array.from(files).map(async (file) => {
      const formDataCloud = new FormData();
      formDataCloud.append('file', file);
      formDataCloud.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);

      try {
        const response = await fetch(CLOUDINARY_API_URL, {
          method: 'POST',
          body: formDataCloud,
        });
        const data = await response.json();
        return {
          id: data.public_id,
          url: data.secure_url,
          name: file.name,
        };
      } catch (err) {
        console.error('Upload failed:', err);
        return null;
      }
    });

    const results = await Promise.all(uploadPromises);
    const successfulUploads = results.filter((result) => result !== null);

    if (successfulUploads.length > 0) {
      setUploadedPhotos((prev) => [...prev, ...successfulUploads]);
    }

    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openUpdateDrawer = (order) => {
    setSelectedOrder(order);
    setUploadedPhotos([]);
    setUpdateDrawerOpen(true);
  };

  const handleConfirmUpdate = useCallback(async () => {
    if (!selectedOrder || uploadedPhotos.length === 0) return;

    setPendingId(selectedOrder.firestoreId);

    try {
      const orderRef = doc(db, 'orderList', selectedOrder.firestoreId);
      const existingImages = selectedOrder.images || [];
      const newImages = [...existingImages, ...uploadedPhotos];

      await updateDoc(orderRef, {
        images: newImages,
        updatedAt: new Date(),
      });

      setCompletedOrderIds((prev) => new Set([...prev, selectedOrder.firestoreId]));
      setUpdateDrawerOpen(false);
      setSelectedOrder(null);
      setUploadedPhotos([]);
    } catch (err) {
      console.error('Error updating order:', err);
      alert(`Failed to update order: ${err.message}`);
    } finally {
      setPendingId(null);
    }
  }, [selectedOrder, uploadedPhotos]);

  const handleCompleteOrder = useCallback(async (firestoreId) => {
    if (pendingId !== null) return;

    const confirmed = window.confirm('Mark this order as Ready?');
    if (!confirmed) return;

    setPendingId(firestoreId);
    try {
      const orderRef = doc(db, 'orderList', firestoreId);
      await updateDoc(orderRef, {
        serviceType: 'ready',
        orderStatus: NEXT_STATUS,
        updatedAt: new Date(),
      });

      setOrders((prev) => prev.filter((o) => o.firestoreId !== firestoreId));
      setCompletedOrderIds((prev) => {
        const next = new Set(prev);
        next.delete(firestoreId);
        return next;
      });
    } catch (err) {
      console.error('Error completing order:', err);
      alert(`Failed to complete order: ${err.message}`);
    } finally {
      setPendingId(null);
    }
  }, [pendingId]);

  const removePhoto = (photoId) => {
    setUploadedPhotos((prev) => prev.filter((p) => p.id !== photoId));
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      setUpdateDrawerOpen(false);
      setSelectedOrder(null);
      setUploadedPhotos([]);
    }
  };

  if (loading) {
    return (
      <>
        <PageHeader title="Processing Orders" />
        <div className="order-status-container">Loading...</div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader title="Processing Orders" />
        <div className="order-status-container">
          <p className="error">{error}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Processing Orders" />

      <div className="order-status-container">
        <p className="results-count">
          {orders.length} order{orders.length !== 1 ? 's' : ''} in processing
        </p>

        <div className="orders-grid">
          {orders.length === 0 ? (
            <p className="no-results">No orders in processing.</p>
          ) : (
            orders.map((order) => {
              const imageUrl = order.images?.[0]?.url?.trim();
              const variantSummary = order.selectedVariants?.length > 0
                ? order.selectedVariants.map((v) => `${v.size} x ${v.quantity}`).join(', ')
                : null;
              const updateDateLead = computeUpdateDateFromLead(order.completeOrderDate, order.createdAt);
              const updateDateSla = computeUpdateDateFromSla(order.completeOrderDate, order.orderServeSla);

              return (
                <div key={order.firestoreId} className="order-card">
                  <div
                    className="order-image"
                    onClick={() => navigate(`/product/${order.id}`, { state: { product: order } })}
                  >
                    {imageUrl ? (
                      <img src={imageUrl} alt={order.productName} />
                    ) : (
                      <div className="image-placeholder">No Image</div>
                    )}
                  </div>

                  <div className="order-info">
                    <div className="order-header">
                      <h5>{order.category}/{order.productName}</h5>
                      <span className={`status-badge status-${order.orderStatus?.toLowerCase()}`}>
                        {order.orderStatus}
                      </span>
                    </div>
                    <p className="order-spec">
                      {order.specification}/{order.styleType}
                    </p>
                    {variantSummary && (
                      <p className="spec-label">{variantSummary}</p>
                    )}
                    {order.completeOrderDate && (
                      <p className="order-date">
                        Delivery date: {formatDate(order.completeOrderDate)}
                      </p>
                    )}
                    <p className="order-date">
                      Update Date: {updateDateSla ? formatDate(updateDateSla) : '—'}
                    </p>
                    {updateDateLead && (
                      <p className="order-date">
                        Update Date (lead): {formatDate(updateDateLead)}
                      </p>
                    )}
                    <div className="action-btn">
                      {!completedOrderIds.has(order.firestoreId) && (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => openUpdateDrawer(order)}
                          disabled={pendingId !== null}
                        >
                          {pendingId === order.firestoreId ? 'Processing...' : 'Update'}
                        </Button>
                      )}
                      {completedOrderIds.has(order.firestoreId) && (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleCompleteOrder(order.firestoreId)}
                          disabled={pendingId !== null}
                        >
                          {pendingId === order.firestoreId ? 'Moving...' : 'Completed'}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {updateDrawerOpen && selectedOrder && (
        <div className="update-drawer-overlay" onClick={handleOverlayClick} role="dialog" aria-modal="true" aria-labelledby="update-drawer-title">
          <div className="update-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3 id="update-drawer-title">Update Order</h3>
              <button className="close-btn" onClick={() => { setUpdateDrawerOpen(false); setSelectedOrder(null); setUploadedPhotos([]); }} aria-label="Close drawer">
                <X size={20} />
              </button>
            </div>

            <div className="drawer-body">
              <div className="update-order-header">
                <h4>{selectedOrder.category} / {selectedOrder.productName}</h4>
                <p>{selectedOrder.specification} / {selectedOrder.styleType}</p>
              </div>

              <div className="update-photo-section">
                <h4>Product Photos (Min. 1 required)</h4>
                <div className="photo-upload-area">
                  <Camera className="upload-icon" />
                  <p className="upload-text">Add product photos from camera</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => handleImageUpload(e.target.files)}
                    className="photo-upload-input"
                    id="update-photo-upload"
                    disabled={uploading}
                  />
                  <label htmlFor="update-photo-upload" className={`upload-button ${uploading ? 'loading' : ''}`}>
                    {uploading ? 'Uploading...' : 'Add Photos'}
                  </label>
                </div>

                {uploadedPhotos.length > 0 && (
                  <div className="photo-grid">
                    {uploadedPhotos.map((photo) => (
                      <div key={photo.id} className="photo-thumb">
                        <img src={photo.url} alt={photo.name} />
                        <button
                          type="button"
                          className="remove-photo-btn"
                          onClick={() => removePhoto(photo.id)}
                          aria-label="Remove photo"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {uploadedPhotos.length === 0 && (
                  <p className="upload-hint">Please upload at least one photo to mark as Ready.</p>
                )}
              </div>
            </div>

            <div className="drawer-footer">
              <Button
                variant="secondary"
                onClick={() => { setUpdateDrawerOpen(false); setSelectedOrder(null); setUploadedPhotos([]); }}
                disabled={pendingId !== null}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmUpdate}
                disabled={pendingId !== null || uploading || uploadedPhotos.length === 0}
              >
                {pendingId === selectedOrder.firestoreId ? 'Saving...' : 'Save Photos'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Processing;
